import { addDays } from 'date-fns/addDays'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { startOfDay } from 'date-fns/startOfDay'
import { ModelAnalytics } from '../common/organisation'

// the days the analytics charts cover, up to and including today
export const NUMBER_DAYS = 27

export interface DailyAnalytics {
    days: Date[]
    newNodes: number[]
    newEdges: number[]
    nodeChanges: number[]
    edgeChanges: number[]
    deletedNodes: number[]
    deletedEdges: number[]
    // nodes in the organisation at the end of each day
    nodes: number[]
}

const hasCount = (a:ModelAnalytics) => [a.nodes, a.backupNodes, a.projectNodes].some(n => typeof n === 'number')

// adds up the changes saved on each day. The analytics are in date order, and each holds the organisation's node
// count at the time, so a day's count is its last one. Days before the first count are estimated from it; later days
// without one keep the day before's
export const analyticsByDay = (analytics:ModelAnalytics[], now:Date = new Date(), numberDays:number = NUMBER_DAYS):DailyAnalytics => {
    const start = startOfDay(addDays(now, -numberDays))
    const days = Array.from({length: numberDays + 1}, (_, i) => addDays(start, i))
    const zeros = () => days.map(() => 0)
    const result:DailyAnalytics = {
        days, newNodes: zeros(), newEdges: zeros(), nodeChanges: zeros(), edgeChanges: zeros(),
        deletedNodes: zeros(), deletedEdges: zeros(), nodes: zeros(),
    }
    const counted = days.map(() => false)

    ;(analytics || []).forEach(a => {
        const date = new Date(a.createdDate)
        const i = differenceInCalendarDays(date, start)
        // outside the days shown (a clock or time zone slightly different from the server's)
        if (isNaN(i) || i < 0 || i >= days.length) return

        result.nodeChanges[i] += a.nodeChanges || 0
        result.edgeChanges[i] += a.edgeChanges || 0
        result.deletedNodes[i] += a.deletedNodes || 0
        result.deletedEdges[i] += a.deletedEdges || 0
        result.newNodes[i] += a.newNodes || 0
        result.newEdges[i] += a.newEdges || 0
        if (hasCount(a)) {
            result.nodes[i] = (a.nodes || 0) + (a.backupNodes || 0) + (a.projectNodes || 0)
            counted[i] = true
        }
    })

    const first = counted.indexOf(true)
    if (first >= 0) {
        const before = Math.max(0, result.nodes[first] - (result.newNodes[first] - result.deletedNodes[first]))
        for (let i = 0; i < first; i++) result.nodes[i] = before
        for (let i = first + 1; i < days.length; i++) {
            if (!counted[i]) result.nodes[i] = result.nodes[i - 1]
        }
    }
    return result
}
